import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { styled, InputBase, Grid, FormLabel, Box, IconButton } from "@mui/material";
import { Formik } from "formik";
import moment from "jalali-moment";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { PALETTE } from "../../Theme/Palette";

const DesignedInput = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    "&::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
    },
    "&::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
    },
    "&[type=number]": {
      MozAppearance: "textfield",
    },
    position: "relative",
    height: "20px",
    color: PALETTE.third,
    backgroundColor: PALETTE.primary,
    textAlign: "right",
    borderStyle: "none",
    borderRadius: "0.5rem",
    fontSize: "0.88rem",
    fontWeight: "bold",
    padding: "10px 0.7rem",
    "&:disabled , &.Mui-disabled": {
      WebkitTextFillColor: PALETTE.sixth,
      color: PALETTE.sixth,
      backgroundColor: PALETTE.fifth,
    },
  },
}));

export function TextInput({
  placeholder,
  name,
  id,
  label,
  type = "text",
  value,
  disabled,
  containerGridSize = { xs: 12, sm: 6 },
  labelGridSize = { xs: 5 },
  inputGridSize = { xs: 7 },
  validationType = "",
  error = false,
  onChange = () => {},
  onClick = () => {},
  onFocus = () => {},
  reapetedValue = "",
  onErrorMessage = () => {},
  isBirthDate = false,
}) {
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(
    (!value || value === undefined || value === "") && error
  );
  const YEAR = moment().format("jYYYY/jMM/jDD").split("/")[0];

  const handleType = () => {
    if (type === "password") {
      return passwordVisibility ? "text" : "password";
    }
  };

  const checkNationalCode = (code) => {
    code = [...code].reverse().join("");
    let sum = 0;
    let all_the_same = true;
    code
      .split("")
      .slice(1)
      .forEach((number, i) => {
        if (parseInt(number) !== parseInt(code[0])) {
          all_the_same = false;
        }
        sum += number * (i + 2);
      });
    if (sum % 11 < 2) {
      return parseInt(code[0]) === sum % 11 && !all_the_same;
    } else {
      return parseInt(code[0]) === 11 - (sum % 11) && !all_the_same;
    }
  };

  const handleValidate = () => {
    const errors = {};
    if (value !== "" && value !== undefined) {
      if (type === "number") {
        if (validationType === "nationalCode") {
          if (/^\d{10}$/.test(value) && checkNationalCode(value)) {
            errors[id] = "";
            onErrorMessage({ [id]: "" });
          } else {
            errors[id] = "کد ملی اشتباه است.";
            onErrorMessage({ [id]: "کد ملی اشتباه است." });
          }
        } else if (validationType === "year") {
          if (!/^\d{4}$/.test(value) || parseInt(YEAR) < parseInt(value)) {
            errors[id] = "سال اشتباه است.";
            onErrorMessage({ [id]: "سال اشتباه است." });
          } else {
            errors[id] = "";
            onErrorMessage({ [id]: "" });
          }
        }
        if (validationType === "phoneNumber") {
          if (!/^09\d{9}$/.test(value)) {
            errors[id] = "شماره موبایل اشتباه است.";
            onErrorMessage({ [id]: "شماره موبایل اشتباه است" });
          } else {
            errors[id] = "";
            onErrorMessage({ [id]: "" });
          }
        }
      }
      if (type === "text") {
        if (validationType === "email") {
          if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
            errors[id] = "آدرس ایمیل اشتباه است.";
            onErrorMessage({ [id]: ".آدرس ایمیل اشتباه است" });
          } else {
            errors[id] = "";
            onErrorMessage({ [id]: "" });
          }
        }
      }
    } else {
      errors[id] = "";
      onErrorMessage({ [id]: "" });
    }
    return errors;
  };

  const toEnglishDigits = (str = "") => {
    if (typeof str === "string") return str?.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
    else return str[0];
  };

  useEffect(() => {
    if (value === "") {
      setShowError(false);
    }
  }, [value, showError]);

  useEffect(() => {
    const normalizedValue = toEnglishDigits(value);

    if (reapetedValue === normalizedValue) {
      setShowSubmitError(normalizedValue === reapetedValue && error);
    } else {
      setShowSubmitError((!value || value === undefined || value === "") && error);
    }
  }, [error, value, reapetedValue]);

  return (
    <Grid
      container
      item
      {...containerGridSize}
      alignItems="center"
      direction="row-reverse"
      sx={{
        boxSizing: "border-box",
        paddingLeft: { xs: 0, sm: "0.5rem", md: "1rem" },
        marginBottom: "0.7rem",
      }}
    >
      {label && (
        <Grid item {...labelGridSize}>
          <FormLabel
            htmlFor={id}
            sx={{
              textAlign: "right",
              color: "#4C466F !important",
              fontSize: "14px",
            }}
          >
            {label}
          </FormLabel>
        </Grid>
      )}
      <Grid item {...inputGridSize}>
        <Formik initialValues={{ [id]: value }} validate={handleValidate}>
          {({ errors, handleBlur }) => (
            <>
              <DesignedInput
                disabled={disabled}
                name={name}
                id={id}
                type={handleType()}
                placeholder={placeholder}
                value={value}
                onChange={(event) => {
                  if (type === "number") {
                    if (/^\d+$/.test(event.target.value) || event.target.value.length === 0) {
                      onChange(event);
                      setShowSubmitError(false);
                    } else {
                      event.target.value = "";
                    }
                  } else {
                    setShowSubmitError(false);
                    onChange(event);
                  }
                }}
                sx={{
                  flex: 1,
                  width: "100%",
                  direction: type === "password" ? "ltr" : "rtl",
                  borderRadius: "0.5rem",
                  border: disabled
                    ? "1px solid transparent"
                    : (errors[id]?.length > 0 && showError) || showSubmitError
                    ? "1px solid #FF0000"
                    : "1px solid " + PALETTE.fourth,
                }}
                onBlur={async (event) => {
                  if (value !== "") await setShowError(true);
                  await handleBlur(event);
                }}
                onClick={onClick}
              />
              {type === "password" && (
                <IconButton
                  onClick={setPasswordVisibility.bind(this, (prev) => !prev)}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "0",
                    transform: "translate(0,-50%)",
                  }}
                >
                  {passwordVisibility ? (
                    <AiFillEyeInvisible fontSize="1.1rem" />
                  ) : (
                    <AiFillEye fontSize="1.1rem" />
                  )}
                </IconButton>
              )}
              <Box
                width="100%"
                sx={{ direction: "rtl", color: "#FF0000", fontSize: "0.7rem", fontWeight: "500" }}
              >
                {showError && errors[id]}
              </Box>
            </>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}

TextInput.propTypes = {
  placeholder: PropTypes.string,
  name: PropTypes.oneOfType([PropTypes.string]),
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
  type: PropTypes.oneOf(["text", "password", "number", "tel"]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
};
